/**
 * Microphone recording for chat voice input, framework free so it unit-tests with fakes.
 *
 * The browser's own speech recognition (`webkitSpeechRecognition`) is not used: Firefox has none and
 * iOS Safari stops it unpredictably. Every target browser can record with `MediaRecorder`, so the clip
 * is recorded here and transcribed by the server, which gives one behaviour and one accuracy everywhere.
 * The codec differs by browser (Chrome, Edge and Firefox write WebM or Ogg with Opus, Safari writes MP4
 * with AAC) and the server accepts all of them.
 */

/** Matches the server limit (`SPEECH_MAX_DURATION_SECONDS`). Recording stops itself here. */
export const VOICE_MAX_DURATION_MS = 120_000;

/** Shorter than this is almost always an accidental tap; nothing is sent. */
export const VOICE_MIN_DURATION_MS = 600;

/**
 * Peak sample level (0 to 1) a clip must reach to count as having sound. A muted or dead microphone
 * stays near 0; quiet speech at arm's length still peaks well above 0.02. Set low on purpose: refusing
 * a real recording costs the user more than one silent clip costs the account.
 */
export const VOICE_SILENCE_PEAK = 0.02;

/** Low enough for speech, so two minutes is under 0.5 MB on a phone connection. */
const AUDIO_BITS_PER_SECOND = 32_000;

/** First supported wins. Opus in WebM is the smallest; MP4 is what Safari records. */
const PREFERRED_TYPES = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/ogg;codecs=opus', 'audio/webm'];

export type VoiceSupport = 'ok' | 'insecure' | 'unsupported';

/** Whether this page can record at all, checked before showing the microphone as usable. */
export function voiceInputSupport(win: Window = window): VoiceSupport {
    if (!win.isSecureContext) {
        return 'insecure';
    }
    const recorder = (win as Window & { MediaRecorder?: unknown }).MediaRecorder;
    if (!win.navigator.mediaDevices?.getUserMedia || typeof recorder !== 'function') {
        return 'unsupported';
    }
    return 'ok';
}

/** The recording type to ask for, or '' to let the browser pick its default. */
export function pickRecordingType(isSupported: (type: string) => boolean): string {
    return PREFERRED_TYPES.find(type => isSupported(type)) ?? '';
}

/** Why a recording could not start, from the `DOMException` name `getUserMedia` throws. */
export type MicrophoneFailure = 'denied' | 'no-device' | 'busy' | 'insecure' | 'failed';

export function classifyMicrophoneError(err: unknown): MicrophoneFailure {
    const name = (err as { name?: string } | null)?.name ?? '';
    if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        return 'denied';
    }
    if (name === 'NotFoundError' || name === 'DevicesNotFoundError' || name === 'OverconstrainedError') {
        return 'no-device';
    }
    if (name === 'NotReadableError' || name === 'TrackStartError' || name === 'AbortError') {
        return 'busy';
    }
    if (name === 'SecurityError') {
        return 'insecure';
    }
    return 'failed';
}

export interface VoiceClip {
    blob: Blob;
    durationMs: number;
    /** Highest sample level seen, or null when the browser gave no level meter. */
    peak: number | null;
}

export interface VoiceRecorderEvents {
    /** About ten times a second while recording: elapsed time and the current level (0 to 1). */
    onTick?: (elapsedMs: number, level: number) => void;
    /** The recording reached `VOICE_MAX_DURATION_MS` and stopped itself. */
    onLimit?: () => void;
}

/**
 * One recording. `start()` asks for the microphone; `stop()` resolves with the clip; `cancel()` throws
 * it away. Either way the microphone is released, so the browser's recording indicator goes out.
 */
export class VoiceRecorder {
    private _stream: MediaStream | null = null;
    private _recorder: MediaRecorder | null = null;
    private _chunks: Blob[] = [];
    private _startedAt = 0;
    private _peak: number | null = null;
    private _audioContext: AudioContext | null = null;
    private _analyser: AnalyserNode | null = null;
    private _samples: Float32Array | null = null;
    private _timer: ReturnType<typeof setInterval> | null = null;
    private _stopped: Promise<VoiceClip> | null = null;
    private _cancelled = false;

    constructor(
        private readonly _events: VoiceRecorderEvents = {},
        private readonly _win: Window = window,
    ) {}

    /** Throws the `getUserMedia` error as-is; classify it with `classifyMicrophoneError`. */
    async start(): Promise<void> {
        const stream = await this._win.navigator.mediaDevices.getUserMedia({
            audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        });
        this._stream = stream;
        const Recorder = (this._win as Window & { MediaRecorder: typeof MediaRecorder }).MediaRecorder;
        const mimeType = pickRecordingType(type => Recorder.isTypeSupported?.(type) ?? false);
        try {
            this._recorder = new Recorder(stream, { ...(mimeType ? { mimeType } : {}), audioBitsPerSecond: AUDIO_BITS_PER_SECOND });
        } catch {
            // Some engines refuse an explicit bitrate or type they claimed to support; their default works.
            this._recorder = new Recorder(stream);
        }
        this._recorder.ondataavailable = (event: BlobEvent) => {
            if (event.data?.size) {
                this._chunks.push(event.data);
            }
        };
        this._meter(stream);
        this._startedAt = Date.now();
        // A timeslice makes the browser hand over data as it goes, so a long clip is not lost if the
        // page is backgrounded on iOS before stop() gets its final chunk.
        this._recorder.start(1000);
        this._timer = setInterval(() => this._tick(), 100);
    }

    get elapsedMs(): number {
        return this._startedAt ? Date.now() - this._startedAt : 0;
    }

    stop(): Promise<VoiceClip> {
        if (this._stopped) {
            return this._stopped;
        }
        const recorder = this._recorder;
        this._stopped = new Promise<VoiceClip>((resolve, reject) => {
            if (!recorder || recorder.state === 'inactive') {
                this._release();
                reject(new Error('not recording'));
                return;
            }
            const durationMs = this.elapsedMs;
            recorder.onstop = () => {
                const type = recorder.mimeType || this._chunks[0]?.type || '';
                const blob = new Blob(this._chunks, { type });
                this._release();
                resolve({ blob, durationMs, peak: this._peak });
            };
            recorder.onerror = () => {
                this._release();
                reject(new Error('recorder failed'));
            };
            recorder.stop();
        });
        return this._stopped;
    }

    cancel(): void {
        this._cancelled = true;
        if (this._recorder && this._recorder.state !== 'inactive') {
            this._recorder.onstop = null;
            this._recorder.stop();
        }
        this._release();
    }

    get cancelled(): boolean {
        return this._cancelled;
    }

    private _tick(): void {
        let level = 0;
        if (this._analyser && this._samples) {
            this._analyser.getFloatTimeDomainData(this._samples);
            for (const sample of this._samples) {
                level = Math.max(level, Math.abs(sample));
            }
            this._peak = Math.max(this._peak ?? 0, level);
        }
        const elapsed = this.elapsedMs;
        this._events.onTick?.(elapsed, level);
        if (elapsed >= VOICE_MAX_DURATION_MS) {
            this._events.onLimit?.();
        }
    }

    /** Level metering is optional: without it the clip is still sent, only the silence check is skipped. */
    private _meter(stream: MediaStream): void {
        try {
            const Context =
                (this._win as Window & { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }).AudioContext ??
                (this._win as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
            if (!Context) {
                return;
            }
            this._audioContext = new Context();
            const source = this._audioContext.createMediaStreamSource(stream);
            this._analyser = this._audioContext.createAnalyser();
            this._analyser.fftSize = 1024;
            this._samples = new Float32Array(this._analyser.fftSize);
            source.connect(this._analyser);
            this._peak = 0;
        } catch {
            this._analyser = null;
            this._peak = null;
        }
    }

    private _release(): void {
        if (this._timer) {
            clearInterval(this._timer);
            this._timer = null;
        }
        this._stream?.getTracks().forEach(track => track.stop());
        this._stream = null;
        this._audioContext?.close().catch(() => undefined);
        this._audioContext = null;
        this._analyser = null;
    }
}
